import { useQuery } from '@tanstack/react-query'
import { API_PATHS } from '@/core/api/api-paths'
import { restClient } from '@/core/api/rest-client'
import { userQueryOptions } from '@/core/auth/user-query'

/**
 * The client surfaces a lifecycle experiment can be anchored to, as the backend keys them.
 *
 * One entry per surface: two experiments naming the same one would overwrite each other, which is
 * why the Collection has its own rather than sharing the return flow's `planOpened`.
 */
export type LifecycleSurface = 'planOpened' | 'collection' | 'profile'

// `planId` is the anchor: the plan whose opening counts as reaching the
// experiment. Optional per experiment, never per arm — both arms get this entry
// unchanged, so its shape says nothing about which arm the user is on.
export interface LifecycleExperiment {
  name: string
  planId?: string
}

// Shape of `GET /v1/config` — all GrowthBook flags/experiment assignments
// evaluated for the authenticated user. The backend coerces "true"/"false"
// strings to booleans, so boolean flags arrive as real booleans.
export interface UserConfigResponse {
  config: Record<string, unknown>
  lifecycleExperiments?: Partial<Record<LifecycleSurface, LifecycleExperiment>>
}

// Shared so the hook and the app-load prefetch use one cache entry.
export const configQueryOptions = {
  queryKey: ['config'] as const,
  queryFn: () => restClient.get<UserConfigResponse>(API_PATHS.config),
  // Flags are evaluated per session — fetch once and keep, no refetch churn.
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: Number.POSITIVE_INFINITY,
  retry: 1,
}

// Requires an authenticated session (restClient attaches the bearer token).
// Only mount inside ProtectedRoute / when isAuthenticated is true.
//
// Gated on the "me" query so /config is NEVER issued before the authed user is
// established — the backend identifies experiment assignments in Amplitude keyed
// off that user, so the ordering must hold in 100% of cases (incl. a direct
// deep-link into a gated route). We observe the shared ['user'] cache here
// without issuing our own fetch — FeatureFlagsPrefetch / pages own fetching it.
export function useConfig() {
  const me = useQuery({ ...userQueryOptions, enabled: false })
  const query = useQuery({ ...configQueryOptions, enabled: me.isSuccess })
  // A disabled query reports isLoading:false; while the me-gate hasn't settled,
  // surface loading so gated callers keep waiting instead of flashing control.
  return { ...query, isLoading: query.isLoading || !me.isFetched }
}

// The experiment this user is in on a given surface, undefined when they are in
// none. Pure observer — FeatureFlagsPrefetch owns issuing /config, so reading it
// from anywhere cannot pull the request ahead of the "me" query it is gated on.
export function useLifecycleExperiment(
  surface: LifecycleSurface,
): LifecycleExperiment | undefined {
  const { data } = useQuery({ ...configQueryOptions, enabled: false })
  return data?.lifecycleExperiments?.[surface]
}
